import * as React from "react";

import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import { cn } from "@/lib/utils";

export interface SelectOption {
  label: string;
  value: string;
  disabled?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
}

interface SelectProps {
  options: SelectOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  searchValue?: string;
  onSearch?: (value: string) => void;
  allowClear?: boolean;
  onClear?: () => void;
  disabled?: boolean;
  error?: string | boolean;
}

export function Select({
  options,
  value,
  onValueChange,
  placeholder,
  className,
  searchValue,
  onSearch,
  allowClear = false,
  onClear,
  disabled = false,
  error,
}: Readonly<SelectProps>) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [internalSearchValue, setInternalSearchValue] = React.useState("");

  const currentSearchValue = searchValue ?? internalSearchValue;

  const handleSearch = (value: string) => {
    if (onSearch) {
      onSearch(value);
    } else {
      setInternalSearchValue(value);
    }
  };

  const handleSelect = (selectedValue: string) => {
    if (selectedValue === value) {
      if (allowClear) {
        onValueChange?.("");
        onClear?.();
      }
    } else {
      onValueChange?.(selectedValue);
    }
    setOpen(false);
  };

  const handleClear = (
    e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    onValueChange?.("");
    onClear?.();
  };

  const filteredOptions = React.useMemo(() => {
    if (!currentSearchValue) {
      return options;
    }
    return options.filter(option =>
      option.label.toLowerCase().includes(currentSearchValue.toLowerCase()),
    );
  }, [options, currentSearchValue]);

  const selectedOption = options.find(option => option.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between truncate text-left font-normal hover:bg-inherit",
            !!error && "border-destructive focus-visible:ring-destructive",
            className,
          )}
          disabled={disabled}
        >
          <div className="flex min-w-0 flex-1 items-center">
            {selectedOption ? (
              <div className="flex min-w-0 flex-1 items-center gap-2">
                {selectedOption.icon && (
                  <selectedOption.icon className="text-muted-foreground h-4 w-4 shrink-0" />
                )}
                <span className="truncate">{selectedOption.label}</span>
              </div>
            ) : (
              <span className="text-muted-foreground truncate">
                {placeholder ?? t("COMMON.SELECT_PLACEHOLDER")}
              </span>
            )}
          </div>
          <div className="ml-2 flex shrink-0 items-center gap-1">
            {allowClear && value && (
              <button
                type="button"
                className="hover:bg-destructive hover:text-destructive-foreground flex h-4 w-4 cursor-pointer items-center justify-center rounded-full transition-colors"
                onClick={handleClear}
                aria-label={t("COMMON.CLEAR_SELECTION")}
              >
                <X className="h-3 w-3" />
              </button>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) max-w-(--radix-popover-trigger-width) p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              className="placeholder:text-muted-foreground flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50"
              placeholder={t("COMMON.SEARCH_OPTIONS")}
              value={currentSearchValue}
              onChange={e => handleSearch(e.target.value)}
            />
          </div>
          <CommandList className="max-h-60 overflow-y-auto">
            <CommandEmpty>{t("COMMON.NO_OPTION")}</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map(option => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => handleSelect(option.value)}
                  disabled={option.disabled}
                  className={cn(option.disabled && "cursor-not-allowed opacity-50")}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {option.icon && <option.icon className="text-muted-foreground mr-2 h-4 w-4" />}
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
